import React, { useCallback, useState } from 'react';
import ReactEcharts from 'echarts-for-react';
import { SeriePointT, TwoDimGraphProps } from '../../types';
import { ChartSettingsT } from 'business/definitions';
import { useSetDataZoomInterval } from 'helpers/chart/dataZoom';
import { HorizontalBarChartOption } from '../ChartOption/HorizontalBarChartOption';
import { useSetLegendDimension } from '../hooks/legendDimension';
import {
    getDatasetPoint,
    getDatasetValueKey,
} from 'components/ChartContainer/components/helpers/datasetHelpers';
import { isContinuous, isDateOrTime } from 'helpers/chartHelper';
import { getHistogramRawDataFilterWithInterval } from '../helpers/barChart';

const getXValue = (
    params: SeriePointT,
    useDataset: boolean,
): ResponseDataValueT => {
    if (useDataset) {
        return params.value[getDatasetValueKey(params, true)] as number;
    }
    return params.data[1];
};

const HorizontalBar = ({
    settings,
    data,
    height,
    width,
    orderedColumns,
    isPdfModeActivated,
    isPngModeActivated,
    onClick,
    isChartViewedOnDashboard,
}: TwoDimGraphProps): React.ReactElement => {
    const [dataZoomInterval, setDataZoomInterval] = useState<
        ChartSettingsT['dataZoomInterval']
    >(settings?.dataZoomInterval);
    const setDataZoomIntervalListener = useSetDataZoomInterval(
        dataZoomInterval,
        setDataZoomInterval,
    );

    const chartOption = new HorizontalBarChartOption(
        orderedColumns,
        settings,
        data,
        width,
        height,
        dataZoomInterval,
        isPdfModeActivated,
        isPngModeActivated,
        !!isChartViewedOnDashboard,
    );
    const option = chartOption.getChartOption();
    // @ts-expect-error [typing] we do set interval in some condition.
    const interval = option.yAxis?.interval;

    useSetLegendDimension(
        orderedColumns.y,
        orderedColumns.z,
        // @ts-expect-error [type] doesn't expect the echart type but ours.
        option.series,
        chartOption.treeValueTranslations,
    );

    const onChartClick = useCallback(
        (params: SeriePointT) => {
            const xValue = getXValue(params, true);
            if (xValue) {
                const { x } = orderedColumns;
                const point: ResponseDataT | undefined = getDatasetPoint(
                    params,
                    orderedColumns,
                );
                if (point) {
                    const isHistogram =
                        isContinuous(x.type) && !isDateOrTime(x.type);
                    const intervalValue = params.value[x.key];
                    if (isHistogram && interval) {
                        point.filter = getHistogramRawDataFilterWithInterval({
                            interval,
                            intervalValue,
                            xKey: x.key,
                            xValue,
                        });
                    }
                }
                onClick(point, x.key);
            }
        },
        [onClick, interval, orderedColumns],
    );

    return (
        <ReactEcharts
            option={option}
            // the notMerge boolean allow the chart to remove all the deprecated components
            // and to replace them with new ones based on the option
            // see https://echarts.apache.org/en/api.html#echartsInstance.setOption
            notMerge
            onEvents={{
                click: onChartClick,
                dataZoom: setDataZoomIntervalListener,
            }}
            style={{ height, width }}
            className="bar-chart e2e-bar-chart"
            opts={{ renderer: 'canvas' }}
        />
    );
};

export default HorizontalBar;
